import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import Input from "@/components/shared/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateAdminMutation,
  useGetAdminByIdQuery,
  useUpdateAdminMutation,
} from "@/features/models/adminsApi";
import { createUserSchema, updateUserSchema } from "@/schemas/user";
import LoadingDots from "@/components/shared/LoadingDots";

const UserForm = ({ edit = false }) => {
  const { id, role } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  if (currentUser.role !== "admin") {
    navigate("/dashboard");
  }

  const { data: user, isLoading: isUserLoading } = useGetAdminByIdQuery(id, {
    skip: !edit || !id,
  });

  const [addUser, { isLoading: isAdding }] = useCreateAdminMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateAdminMutation();

  const form = useForm({
    resolver: zodResolver(edit ? updateUserSchema : createUserSchema),
    defaultValues: async () => {
      if (edit && user) {
        return {
          email: user.email ?? "",
          password: "",
          confirmPassword: "",
          role: user.role ?? "user",
        };
      }
      return {
        email: "",
        password: "",
        confirmPassword: "",
        role: "user",
      };
    },
  });

  useEffect(() => {
    if (edit && user) {
      form.reset({
        email: user.email ?? "",
        password: "",
        confirmPassword: "",
        role: user.role ?? "user",
      });
    }
  }, [edit, user, form]);

  const onSubmit = async (data) => {
    try {
      const payload = { ...data };
      delete payload.confirmPassword;

      if (edit && (!payload.password || payload.password.trim() === "")) {
        delete payload.password;
      }

      if (edit) {
        await updateUser({ id, ...payload }).unwrap();
        toast.success("Admin updated successfully");
      } else {
        await addUser(payload).unwrap();
        toast.success("Admin created successfully");
      }

      navigate("/dashboard/manage-users/" + role);
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error(error?.data?.message || "Failed to save user");
    }
  };

  if (edit && isUserLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingDots />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle>{edit ? "Edit Admin" : "Create New Admin"}</CardTitle>
          <CardDescription>
            {edit
              ? "Update user information"
              : "Fill in the details to create a new user account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form} key={edit && user ? user.id || user.email : "create"}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Email */}
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    {...form.register("email")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>

              {/* Role (using ShadCN Select) */}
              <FormItem>
                <FormLabel>Role</FormLabel>
                <FormControl>
                  <Select
                    className="w-full"
                    onValueChange={(value) => form.setValue("role", value)}
                    value={form.watch("role")}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="user">Guest</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>

              {/* Password */}
              <FormItem>
                <FormLabel>
                  Password{" "}
                  {edit && (
                    <span className="text-sm text-muted-foreground font-normal">
                      (leave blank to keep current password)
                    </span>
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={edit ? "Enter new password" : "Enter password"}
                    {...form.register("password")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>

              {/* Confirm Password */}
              <FormItem>
                <FormLabel>
                  Confirm Password{" "}
                  {edit && (
                    <span className="text-sm text-muted-foreground font-normal">
                      (required if changing password)
                    </span>
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={
                      edit ? "Confirm new password" : "Confirm password"
                    }
                    {...form.register("confirmPassword")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>

              {/* Actions */}
              <div className="flex items-center gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={isAdding || isUpdating}
                  className="flex items-center gap-2"
                >
                  {(isAdding || isUpdating) && <LoadingDots />}
                  {edit ? "Update Admin" : "Create Admin"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/dashboard/manage-users/" + role)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserForm;
